import React from "react";
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  Fab,
  IconButton,
  InputAdornment,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { ArrowBack as ArrowBackIcon, Sync as SyncIcon, Clear as ClearIcon } from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import songsAPI from "restApi/songsApi";
import { extractYoutubeId, cleanYoutubeTitle } from "utils/youtube";
import { extractSpotifyId, cleanSpotifyTitle } from "utils/spotify";
import { addedLyrics } from "utils/catalogProgress";
import { AUTOFILL_TIP_DISMISSED_KEY } from "CONSTANTS";

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: theme.palette.background.paper,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
  },
  title: {
    flexGrow: 1,
    marginLeft: theme.spacing(1),
  },
  content: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  description: {
    marginBottom: theme.spacing(3),
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(3),
  },
}));

// Add and edit share this one page (not a modal). `song` is either the
// song being edited or a blank shape (see SongCreateContainer.jsx) when
// creating. `embedded` drops the page's own top bar, for showing the form
// inside another page (the desktop catalog's detail pane).
export default ({ song, onSaved, onCancel, directToCatalog, embedded }) => {
  const classes = useStyles();
  const isCreate = !song.id;
  // directToCatalog (leader/admin adding straight to the catalog) isn't
  // a proposal, so it gets its own copy rather than reusing "propose" -
  // matches the terminology Catalog.jsx's own add button already uses.
  // The explanatory line below the title exists mainly for anyone who
  // lands here via the YouTube/Spotify share-to-propose flow with no
  // other context for where they are or why.
  const pageTitle = !isCreate ? "Edit song" : directToCatalog ? "Add song to catalog" : "Propose a new song";
  const pageDescription = !isCreate
    ? "Update this song's details below."
    : directToCatalog
    ? "Add a song straight to your team's catalog."
    : "Suggest a song for your team to learn - once you submit it, a team leader can review and approve it into the catalog.";
  const [data, setData] = React.useState({
    title: song.title || "",
    artist: song.artist || "",
    youtube_link: song.youtube_link || "",
    spotify_id: song.spotify_id ? `spotify:track:${song.spotify_id}` : "",
    lyrics: song.lyrics || "",
  });
  // A one-time nudge toward autofill/share-to-propose, shown only on an
  // actual proposal (not editing an existing song, not the leader/admin
  // "add straight to catalog" flow - directToCatalog skips it same as
  // pageTitle/pageDescription above already do). Dismissing is
  // permanent - see AUTOFILL_TIP_DISMISSED_KEY.
  const [showAutofillTip, setShowAutofillTip] = React.useState(
    isCreate && !directToCatalog && !localStorage.getItem(AUTOFILL_TIP_DISMISSED_KEY)
  );
  const dismissAutofillTip = () => {
    localStorage.setItem(AUTOFILL_TIP_DISMISSED_KEY, "1");
    setShowAutofillTip(false);
  };

  const [errors, setErrors] = React.useState({});
  // Which fields the user has actually interacted with - a client-side
  // validation error only ever displays for a touched field, so a
  // fresh/empty form doesn't show as broken before anyone's typed
  // anything. Server errors (handleSubmit's catch, below) bypass this -
  // those only ever arrive after a real submit attempt, so they should
  // always be visible regardless of which fields happen to be touched.
  const [touched, setTouched] = React.useState({});
  const [search, setSearch] = React.useState({ inProgress: false, status: null });
  const [youtubeLookup, setYoutubeLookup] = React.useState(false);
  const [spotifyLookup, setSpotifyLookup] = React.useState(false);

  // Mirrors the format checks SongUnknownSerializer actually enforces
  // (api/serializers.py) - catches an obviously-bad value as it's
  // typed, not a replacement for server-side validation (duplicate
  // title/artist, for one, can't be checked without asking the server
  // anyway).
  const validateField = (name, value) => {
    switch (name) {
      case "title":
        return value.trim() ? null : "This field is required.";
      case "youtube_link":
        return !value || extractYoutubeId(value) ? null : "This is not a valid YouTube link.";
      case "spotify_id":
        return !value || extractSpotifyId(value) ? null : "This is not a valid Spotify link.";
      default:
        return null;
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setData((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => {
      const message = validateField(name, value);
      const next = { ...prev };
      if (message) {
        next[name] = [message];
      } else {
        delete next[name];
      }
      return next;
    });
  };

  // Always reflects the latest data without the component re-rendering
  // just to update it - lets the debounced effect below (and the fetch
  // it kicks off) read the *current* title/artist at the moment it
  // actually runs, not whatever was in scope when it was scheduled.
  const dataRef = React.useRef(data);
  dataRef.current = data;

  // Whatever oEmbed request is currently in flight, if any - a newer
  // link (typed, pasted, or the force button) always supersedes an
  // older one, so this is aborted before starting a fresh request
  // rather than letting a slow, stale response win a race against a
  // faster, newer one.
  const youtubeRequestRef = React.useRef(null);

  // Same "newer request wins" idea as youtubeRequestRef, but as a plain
  // counter rather than an AbortController - the axios version pinned
  // here predates reliable `signal` support, so a stale response is
  // just ignored at resolution time instead of actually being cancelled
  // in flight.
  const spotifyRequestIdRef = React.useRef(0);

  // Same idea again, for the YouTube-triggered Spotify cross-search
  // (crossFillSpotify, below).
  const spotifySearchRequestIdRef = React.useRef(0);

  // Shared by the manual search button (searchForLyrics, below), the
  // automatic YouTube-triggered lookup (runYoutubeLookup) and the
  // Spotify cross-fill's own retry (crossFillSpotify) - takes title/
  // artist explicitly rather than reading them off `data`, since the
  // automatic paths need to use values they just resolved, before
  // they've necessarily landed in state yet. Returns whether lyrics
  // were actually found, so a caller can chain a fallback attempt.
  const fetchLyrics = (title, artist) => {
    setSearch({ inProgress: true, status: null });
    return songsAPI.createLyrics({ title, artist }).then(
      (res) => {
        setData((prev) => ({ ...prev, lyrics: res.data }));
        setSearch({ inProgress: false, status: 200 });
        return true;
      },
      () => {
        setSearch({ inProgress: false, status: 404 });
        return false;
      }
    );
  };

  // After a YouTube link resolves title/artist, also looks for the same
  // track on Spotify (a search, not a link lookup - see
  // SpotifyTrackSearch in api/views.py) and fills the Spotify field
  // from it too - same "never clobber, unless forced" rule as
  // everything else here. Spotify's own metadata is more reliable for
  // the lyrics lookup than a YouTube video title ever is (cleaner,
  // structured title/artist, no video-title noise - confirmed while
  // testing this feature live), so if the YouTube-triggered lyrics
  // fetch above didn't find anything, this retries once using
  // Spotify's own (title, artist) instead.
  const crossFillSpotify = (title, artist, force, lyricsFound) => {
    if (!force && dataRef.current.spotify_id) return;
    if (!title) return;

    const requestId = ++spotifySearchRequestIdRef.current;
    songsAPI.searchSpotifyTrack(title, artist).then(
      (res) => {
        if (spotifySearchRequestIdRef.current !== requestId) return;
        const { spotify_id: foundId, title: spotifyTitle, artist: spotifyArtist } = res.data;
        setData((prev) => {
          if (!force && prev.spotify_id) return prev;
          return { ...prev, spotify_id: `spotify:track:${foundId}` };
        });

        const retryLyricsWithSpotifyData = () => {
          if (!dataRef.current.lyrics) {
            fetchLyrics(cleanSpotifyTitle(spotifyTitle), spotifyArtist);
          }
        };
        if (lyricsFound && typeof lyricsFound.then === "function") {
          lyricsFound.then((found) => {
            if (!found) retryLyricsWithSpotifyData();
          });
        } else if (!lyricsFound) {
          retryLyricsWithSpotifyData();
        }
      },
      () => {} // no match found (404), or the search request itself failed - not worth surfacing, YouTube's own data is still there
    );
  };

  // Fetches title/artist from the video's own metadata via YouTube's
  // public oEmbed endpoint (no API key needed). `force` (the inline
  // button) always overwrites with whatever the current link resolves
  // to - the whole point of it is re-fetching after the link changes.
  // Otherwise (live, as the link is typed/pasted in - see the debounced
  // effect below) it only ever fills fields that are still empty -
  // someone who's already typed a title/artist clearly doesn't want
  // that touched - and the functional setData below re-checks that at
  // resolution time too, in case they started typing while the lookup
  // was still in flight.
  const runYoutubeLookup = (link, force) => {
    if (!force && (dataRef.current.title || dataRef.current.artist)) return;
    const videoId = extractYoutubeId(link);
    if (!videoId) return;

    if (youtubeRequestRef.current) {
      youtubeRequestRef.current.abort();
    }
    const controller = new AbortController();
    youtubeRequestRef.current = controller;

    // Force (the inline button) clears title/artist immediately,
    // before the fetch even resolves - visible confirmation that a
    // refetch is actually happening, rather than the old values just
    // sitting there unchanged until the new ones arrive. Clears any
    // stale error on those fields too (e.g. "This field is required"
    // left over from before) since the values themselves just changed.
    if (force) {
      setData((prev) => ({ ...prev, title: "", artist: "" }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next.title;
        delete next.artist;
        return next;
      });
    }

    setYoutubeLookup(true);
    fetch(
      `https://www.youtube.com/oembed?url=${encodeURIComponent(
        `https://www.youtube.com/watch?v=${videoId}`
      )}&format=json`,
      { signal: controller.signal }
    )
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((info) => {
        const resolvedTitle = cleanYoutubeTitle(info.title);
        const resolvedArtist = info.author_name || "";
        let applied = false;
        setData((prev) => {
          if (!force && (prev.title || prev.artist)) return prev;
          applied = true;
          return { ...prev, title: resolvedTitle, artist: resolvedArtist };
        });
        // Chase lyrics too, but only once title/artist actually landed
        // from this lookup (not skipped above) and only into an empty
        // lyrics field - same "never clobber what's already there" rule
        // as everything else on this page.
        let lyricsFound = null;
        if (applied && resolvedTitle && resolvedArtist && !dataRef.current.lyrics) {
          lyricsFound = fetchLyrics(resolvedTitle, resolvedArtist);
        }
        // Cross-fill the Spotify field too, using whatever title/artist
        // this page ends up with either way (just resolved above, or
        // already there beforehand) - see crossFillSpotify.
        const finalTitle = applied ? resolvedTitle : dataRef.current.title;
        const finalArtist = applied ? resolvedArtist : dataRef.current.artist;
        if (finalTitle) {
          crossFillSpotify(finalTitle, finalArtist, force, lyricsFound);
        }
      })
      .catch(() => {})
      .finally(() => {
        // A response for a request that's since been superseded (this
        // isn't the ref's current controller anymore) shouldn't flip
        // the spinner off behind the newer request's back.
        if (youtubeRequestRef.current === controller) {
          setYoutubeLookup(false);
          youtubeRequestRef.current = null;
        }
      });
  };

  // Live, as the link is typed or pasted in - debounced so a full
  // paste (or a few quick keystrokes) triggers one lookup, not one per
  // keystroke; each new change cancels whatever the previous one
  // already scheduled/started (see runYoutubeLookup's abort above).
  React.useEffect(() => {
    const timer = setTimeout(() => runYoutubeLookup(data.youtube_link, false), 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.youtube_link]);

  // Mirrors runYoutubeLookup above, using Spotify's own track data
  // (title/artists, via a backend endpoint - see SpotifyTrack in
  // api/views.py, which uses the app's own client-credentials token so
  // this works regardless of whether this team has connected its own
  // Spotify account) instead of YouTube's oEmbed. Same "never clobber
  // what's already there unless forced" rule as the YouTube version.
  const runSpotifyLookup = (link, force) => {
    if (!force && (dataRef.current.title || dataRef.current.artist)) return;
    const spotifyId = extractSpotifyId(link);
    if (!spotifyId) return;

    const requestId = ++spotifyRequestIdRef.current;

    if (force) {
      setData((prev) => ({ ...prev, title: "", artist: "" }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next.title;
        delete next.artist;
        return next;
      });
    }

    setSpotifyLookup(true);
    songsAPI.getSpotifyTrack(spotifyId).then(
      (res) => {
        if (spotifyRequestIdRef.current !== requestId) return;
        const resolvedTitle = cleanSpotifyTitle(res.data.title);
        const resolvedArtist = res.data.artist || "";
        let applied = false;
        setData((prev) => {
          if (!force && (prev.title || prev.artist)) return prev;
          applied = true;
          return { ...prev, title: resolvedTitle, artist: resolvedArtist };
        });
        if (applied && resolvedTitle && resolvedArtist && !dataRef.current.lyrics) {
          fetchLyrics(resolvedTitle, resolvedArtist);
        }
      },
      () => {}
    ).finally(() => {
      if (spotifyRequestIdRef.current === requestId) {
        setSpotifyLookup(false);
      }
    });
  };

  // Live, as the link is typed or pasted in - same debounce/race-guard
  // shape as the YouTube effect above.
  React.useEffect(() => {
    const timer = setTimeout(() => runSpotifyLookup(data.spotify_id, false), 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.spotify_id]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const validationErrors = {};
    ["title", "youtube_link", "spotify_id"].forEach((name) => {
      const message = validateField(name, data[name]);
      if (message) validationErrors[name] = [message];
    });
    // A submit attempt is itself an interaction with every validated
    // field, even one never actually touched (e.g. submitting a
    // completely untouched, still-empty form) - so any error found
    // here needs to be visible too, not just live-typed ones.
    setTouched((prev) => ({ ...prev, title: true, youtube_link: true, spotify_id: true }));
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    const request = isCreate
      ? songsAPI.createSong(directToCatalog ? { ...data, status: "approved" } : data)
      : songsAPI.patchSong(song.id, data);
    request
      .then((res) => onSaved(res.data, { addedLyrics: !isCreate && addedLyrics(song, res.data) }))
      .catch((err) => {
        if (err.response && err.response.data) {
          setErrors(err.response.data);
          const serverErrorFields = Object.fromEntries(
            Object.keys(err.response.data).map((field) => [field, true])
          );
          setTouched((prev) => ({ ...prev, ...serverErrorFields }));
        }
      });
  };

  const errorMessages = Object.entries(errors)
    .filter(([field]) => touched[field])
    .map(([field, messages], index) => {
      let text = messages[0];
      if (
        text === "The fields title, artist, proposed_for must make a unique set." ||
        field === "unique_constraint"
      ) {
        text = "This song already exists";
      }
      return (
        <Typography variant="caption" color="error" display="block" key={"error" + index}>
          {text}
        </Typography>
      );
    });

  const searchForLyrics = () => fetchLyrics(data.title, data.artist);

  // The auto-lookup (Genius/lrclib, chased after a YouTube/Spotify
  // autofill resolves title+artist - see runYoutubeLookup/
  // runSpotifyLookup) sometimes lands on a wrong-song match rather than
  // failing outright, especially from Spotify - it has no way to tell
  // "wrong lyrics" from "right lyrics" itself, so this is the way out.
  const clearLyrics = () => {
    setData((prev) => ({ ...prev, lyrics: "" }));
    setSearch({ inProgress: false, status: null });
  };

  const searchable = data.title.length > 2 && data.artist.length > 0 && !search.inProgress;

  return (
    <>
      {!embedded && (
        <AppBar position="sticky" color="default" className={classes.appBar} elevation={1}>
          <Toolbar className={classes.toolbar}>
            <IconButton edge="start" aria-label="cancel" title="Cancel" onClick={onCancel}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" className={classes.title}>
              {pageTitle}
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      <Container maxWidth="md" className={classes.content}>
        {embedded && (
          <Typography variant="h6" gutterBottom>
            {pageTitle}
          </Typography>
        )}
        <Typography variant="body1" color="textSecondary" className={classes.description}>
          {pageDescription}
        </Typography>
        {showAutofillTip && (
          <Alert severity="info" onClose={dismissAutofillTip} className={classes.field}>
            Paste a YouTube or Spotify link below and the title, artist and
            lyrics fill in for you. You can also share a link straight from
            either app to start a proposal like this one.
          </Alert>
        )}
        <Box textAlign="center">{errorMessages}</Box>
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
          <TextField
            name="youtube_link"
            label="YouTube link"
            type="url"
            color="secondary"
            fullWidth
            className={classes.field}
            value={data.youtube_link}
            onChange={handleChange}
            onBlur={() => runYoutubeLookup(data.youtube_link, false)}
            error={touched.youtube_link && "youtube_link" in errors}
            helperText={
              (touched.youtube_link && "youtube_link" in errors && errors.youtube_link) ||
              (youtubeLookup ? "Looking up title/artist..." : "")
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Fill title/artist from this link">
                    <span>
                      <IconButton
                        edge="end"
                        size="small"
                        disabled={!extractYoutubeId(data.youtube_link) || youtubeLookup}
                        onClick={() => runYoutubeLookup(data.youtube_link, true)}
                      >
                        {youtubeLookup ? <CircularProgress size={20} /> : <SyncIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            name="title"
            label="Title"
            color="secondary"
            fullWidth
            className={classes.field}
            value={data.title}
            onChange={handleChange}
            error={touched.title && "title" in errors}
            helperText={touched.title && "title" in errors && errors.title}
            required
          />
          <TextField
            name="artist"
            label="Artist"
            color="secondary"
            fullWidth
            className={classes.field}
            value={data.artist}
            onChange={handleChange}
          />
          <TextField
            name="spotify_id"
            label="Spotify link"
            type="url"
            color="secondary"
            fullWidth
            className={classes.field}
            value={data.spotify_id}
            onChange={handleChange}
            onBlur={() => runSpotifyLookup(data.spotify_id, false)}
            error={touched.spotify_id && "spotify_id" in errors}
            helperText={
              (touched.spotify_id && "spotify_id" in errors && errors.spotify_id) ||
              (spotifyLookup ? "Looking up title/artist..." : "")
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Fill title/artist from this link">
                    <span>
                      <IconButton
                        edge="end"
                        size="small"
                        disabled={!extractSpotifyId(data.spotify_id) || spotifyLookup}
                        onClick={() => runSpotifyLookup(data.spotify_id, true)}
                      >
                        {spotifyLookup ? <CircularProgress size={20} /> : <SyncIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <Box mt={2} style={{ position: "relative" }}>
            <Box style={{ position: "absolute", top: 8, right: 8, zIndex: 3, display: "flex", gap: "8px" }}>
              <Tooltip title="Clear lyrics">
                <span>
                  <Fab size="small" disableFocusRipple disabled={!data.lyrics} onClick={clearLyrics}>
                    <ClearIcon />
                  </Fab>
                </span>
              </Tooltip>
              <Tooltip
                open={search.status === 404}
                onClose={() => setSearch({ inProgress: false, status: null })}
                leaveDelay={2000}
                title="Could not find lyrics. Try again?"
                placement="left"
              >
                <Fab size="small" disableFocusRipple disabled={!searchable} onClick={searchForLyrics}>
                  {search.inProgress ? <CircularProgress color="secondary" size={25} /> : <SearchIcon />}
                </Fab>
              </Tooltip>
            </Box>
            <TextField
              name="lyrics"
              label="Lyrics"
              color="secondary"
              variant="filled"
              minRows={8}
              maxRows={16}
              multiline
              fullWidth
              value={data.lyrics}
              onChange={handleChange}
            />
          </Box>
          <Box className={classes.actions}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="submit" color="secondary">
              Submit
            </Button>
          </Box>
        </form>
      </Container>
    </>
  );
};
